import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

interface AdminSidebarProps {
  activeSection: string;
  activeSubSection?: string | null;
  onSelect: (section: string) => void;
}

const navItems = [
  { id: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
  { id: 'inquiries', label: 'Enquiries', icon: 'ri-mail-line', badge: 38 },
  { id: 'tutors', label: 'Tutor Management', icon: 'ri-user-star-line' },
  { id: 'students', label: 'Students', icon: 'ri-user-line' },
  { id: 'events', label: 'Events', icon: 'ri-calendar-event-line' },
  { id: 'resources', label: 'Resource Management', icon: 'ri-folder-open-line' },
  { id: 'blog', label: 'Blog & Content', icon: 'ri-article-line' },
  { id: 'settings', label: 'Settings', icon: 'ri-settings-3-line' },
];

export default function AdminSidebar({ activeSection, activeSubSection, onSelect }: AdminSidebarProps) {
  const [user, setUser] = useState<{ name?: string, email?: string } | null>(null);
  const [inquiryCount, setInquiryCount] = useState<number>(0);
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error("Failed to parse user from localStorage", e);
      }
    }

    const fetchCounts = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE}/api/inquiries`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const openCount = data.filter((i: any) => i.status === 'open').length;
          setInquiryCount(openCount);
        }
      } catch (error) {
        console.error("Failed to fetch inquiry count", error);
      }
    };

    fetchCounts();
    // Refresh count every 60 seconds 
    const interval = setInterval(fetchCounts, 60000);
    return () => clearInterval(interval);
  }, [API_BASE]);

  const currentNavItems = navItems.map(item =>
    item.id === 'inquiries' ? { ...item, badge: inquiryCount } : item
  );

  const RESOURCE_SUBITEMS = [
    { id: 'past-papers', label: 'Past Papers', icon: 'ri-file-text-line' },
    { id: 'flash-cards', label: 'Flash cards', icon: 'ri-stack-line' },
    { id: 'cheat-sheets', label: 'Cheat sheets', icon: 'ri-clipboard-line' },
    { id: 'quizzes', label: 'Quizzes', icon: 'ri-question-line' },
  ];

  return (
    <aside className="w-60 flex-shrink-0 bg-slate-900 min-h-screen flex flex-col">
      <div className="p-5 border-b border-slate-800">
        <Link to="/" className="flex items-center gap-2 group">
          <img src="/logo.png" alt="GCSE Computing Logo" className="h-8 w-auto object-contain" />
          <span className="font-bold text-sm text-white" style={{ fontFamily: "'Inter', sans-serif" }}>
            GCSE <span className="text-emerald-400">Computing</span>
          </span>
        </Link>
        <div className="mt-3 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center bg-emerald-700/30 rounded-md">
            <i className="ri-shield-line text-emerald-400 text-xs"></i>
          </div>
          <span className="text-xs text-emerald-400 font-semibold uppercase tracking-wider">Admin Panel</span>
        </div>
      </div>

      <nav className="flex-1 p-3 flex flex-col gap-0.5">
        {currentNavItems.map((item) => (
          <div key={item.id}>
            <button
              onClick={() => {
                // If item has sub-items, toggle expansion; otherwise navigate directly
                const hasSub = item.id === 'resources';
                if (hasSub) {
                  const next = expandedId === item.id ? null : item.id;
                  setExpandedId(next);
                } else {
                  onSelect(item.id);
                  try { navigate(`/admin/dashboard/${item.id}`); } catch (e) { }
                }
              }}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer text-left ${activeSection === item.id
                ? 'bg-emerald-700 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
            >
              <span className="flex items-center gap-2.5">
                <i className={`${item.icon} text-base`}></i>
                {item.label}
              </span>
              <div className="flex items-center gap-3">
                {item.badge !== undefined && item.badge > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                    {item.badge}
                  </span>
                )}
                {/* Dropdown indicator for items with submenus */}
                {item.id === 'resources' && (
                  <i className={`ri-arrow-down-s-line text-sm transform transition-transform ${expandedId === item.id || (activeSection === item.id && !!activeSubSection) ? 'rotate-180' : ''}`}></i>
                )}
              </div>
            </button>

            {/* Resource sub-items - show only when parent is expanded OR when URL already points to a subsection */}
            {item.id === 'resources' && (expandedId === item.id || (activeSection === item.id && !!activeSubSection)) && (
              <div className="mt-2 ml-3 flex flex-col gap-1">
                {RESOURCE_SUBITEMS.map((sub) => (
                  <button
                    key={sub.id}
                    onClick={() => {
                      onSelect('resources');
                      try { navigate(`/admin/dashboard/resources/${sub.id}`); } catch (e) { }
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg text-left transition-colors ${activeSubSection === sub.id ? 'bg-slate-100 text-slate-800' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
                  >
                    <i className={`${sub.icon} text-xs`} />
                    <span className="truncate">{sub.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-700 rounded-full flex-shrink-0">
            <i className="ri-user-line text-white text-sm"></i>
          </div>
          <div className="min-w-0">
            <p className="text-white text-xs font-semibold truncate">{user?.name || 'Super Admin'}</p>
            <p className="text-slate-500 text-xs truncate">{user?.email || 'hi@gcsecomputing.co.uk'}</p>
          </div>
        </div>
        <button
          onClick={() => {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }}
          className="w-full flex items-center gap-2 text-slate-500 hover:text-red-400 text-xs transition-colors cursor-pointer bg-transparent border-none p-0 text-left"
        >
          <i className="ri-logout-box-line"></i> Sign Out
        </button>
      </div>
    </aside>
  );
}
